import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import {
    getUserLists,
    createSubscriptionList,
    addAppsToList,
    removeAppsFromList,
    validateApplications
} from './apihelpers';

/**
 * AddToSubscriptionListModal Component
 * A reusable modal for adding applications/CIs to subscription lists
 * 
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal closes
 * @param {object} graphData - User's graph data (displayName, mail)
 * @param {string|array} applications - Single app name or array of CI names to add
 * @param {string} modalTitle - Title for the modal (default: "Add to Subscription List")
 * @param {function} onSuccess - Optional callback after successful operation
 * @param {boolean} allowMultipleSelection - Whether to allow selecting affected CIs (for ChangeDetailsPage)
 * @param {function} getApplicationsList - Optional function to get list of applications (for ChangeDetailsPage affected CIs)
 * @param {string} fontFamily - Optional font family for styling
 */
const AddToSubscriptionListModal = ({
    open,
    onClose,
    graphData,
    applications,
    modalTitle = "Add to Subscription List",
    onSuccess,
    allowMultipleSelection = false,
    getApplicationsList,
    fontFamily = 'inherit'
}) => {
    const [subscriptionLists, setSubscriptionLists] = useState([]);
    const [selectedLists, setSelectedLists] = useState([]);
    const [isAddingToLists, setIsAddingToLists] = useState(false);
    const [loadingLists, setLoadingLists] = useState(false);
    const [showCreateNew, setShowCreateNew] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [newListDescription, setNewListDescription] = useState('');
    const [initialListsWithApp, setInitialListsWithApp] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    // Get applications as array
    const getApplicationsArray = () => {
        if (Array.isArray(applications)) {
            if (allowMultipleSelection) {
                // Always include the main CI (first item)
                const mainCI = applications[0];
                if (selectedItems.length > 0) {
                    // Include main CI + selected affected CIs
                    return [mainCI, ...selectedItems];
                }
                // If nothing selected, only return main CI
                return [mainCI];
            }
            return applications;
        }
        return [applications];
    };

    // Fetch subscription lists
    const fetchSubscriptionLists = async () => {
        setLoadingLists(true);
        try {
            const response = await getUserLists(graphData?.mail);
            if (response.status === 200) {
                const userLists = response.data.lists.filter(list =>
                    (list.owner?.user_name === graphData?.displayName || list.permission === 'write')
                ) || [];
                setSubscriptionLists(userLists);

                // Track initial lists that have any of the apps
                const apps = getApplicationsArray();
                const listsWithAnyApp = userLists
                    .filter(list => list.applications?.some(app => apps.includes(app.app_name)))
                    .map(list => list.list_id);
                setSelectedLists(listsWithAnyApp);
                setInitialListsWithApp(listsWithAnyApp);
            }
        } catch (error) {
            console.error('Error fetching subscription lists:', error);
            toast.error('Failed to load subscription lists', {
                position: 'top-center',
                autoClose: 3000,
            });
        } finally {
            setLoadingLists(false);
        }
    };

    // Load data when modal opens
    useEffect(() => {
        if (open) {
            setShowCreateNew(false);
            setNewListName('');
            setNewListDescription('');
            setSelectedItems([]);
            fetchSubscriptionLists();
        }
    }, [open, applications]);

    // Handle list selection toggle
    const handleListToggle = (listId) => {
        setSelectedLists(prev =>
            prev.includes(listId)
                ? prev.filter(id => id !== listId)
                : [...prev, listId]
        );
    };

    // Handle item selection toggle (for affected CIs)
    const handleItemToggle = (item) => {
        setSelectedItems(prev =>
            prev.includes(item)
                ? prev.filter(i => i !== item)
                : [...prev, item]
        );
    };

    // Check if any changes were made
    const hasChanges = () => {
        if (showCreateNew) return true;
        if (selectedLists.length !== initialListsWithApp.length) return true;
        return !selectedLists.every(id => initialListsWithApp.includes(id)) ||
            !initialListsWithApp.every(id => selectedLists.includes(id));
    };

    // Handle save/add to lists
    const handleAddToLists = async () => {
        const appsToAdd = getApplicationsArray();
        if (appsToAdd.length === 0) {
            toast.warning('No applications found to add', {
                position: 'top-center',
                autoClose: 3000,
            });
            return;
        }

        setIsAddingToLists(true);
        try {
            // Validate applications first
            const validation = await validateApplications(appsToAdd);
            const validApps = validation.valid_applications || [];
            const invalidApps = validation.invalid_applications || [];

            if (validApps.length === 0) {
                toast.error('None of the items are valid applications', {
                    position: 'top-center',
                    autoClose: 3000,
                });
                setIsAddingToLists(false);
                return;
            }

            const validAppNames = validApps.map(app => app.app_name || app.name);
            let successCount = 0;
            let removedCount = 0;
            let createdNewList = false;

            // Create new list if requested
            if (showCreateNew) {
                if (!newListName.trim()) {
                    toast.warning('Please enter a list name', {
                        position: 'top-center',
                        autoClose: 3000,
                    });
                    setIsAddingToLists(false);
                    return;
                }

                try {
                    const response = await createSubscriptionList({
                        userName: graphData?.displayName,
                        userEmail: graphData?.mail,
                        listName: newListName.trim(),
                        appNames: validAppNames,
                        description: newListDescription.trim(),
                    });

                    if (response.status === 200) {
                        createdNewList = true;
                        successCount++;
                    }
                } catch (error) {
                    console.error('Error creating new list:', error);
                    toast.error('Failed to create new list', {
                        position: 'top-center',
                        autoClose: 3000,
                    });
                    setIsAddingToLists(false);
                    return;
                }
            }

            // Determine which lists to add to and which to remove from
            const listsToAdd = selectedLists.filter(id => !initialListsWithApp.includes(id));
            const listsToRemove = initialListsWithApp.filter(id => !selectedLists.includes(id));

            // Add to new lists
            for (const listId of listsToAdd) {
                try {
                    await addAppsToList({
                        userName: graphData?.displayName,
                        userEmail: graphData?.mail,
                        listId,
                        appNames: validAppNames,
                    });
                    successCount++;
                } catch (error) {
                    console.error(`Error adding to list ${listId}:`, error);
                }
            }

            // Remove from deselected lists
            for (const listId of listsToRemove) {
                try {
                    await removeAppsFromList({
                        userName: graphData?.displayName,
                        userEmail: graphData?.mail,
                        listId,
                        appNames: validAppNames,
                    });
                    removedCount++;
                } catch (error) {
                    console.error(`Error removing from list ${listId}:`, error);
                }
            }

            // Show success message
            if (createdNewList && (successCount > 1 || removedCount > 0)) {
                toast.success(
                    `Created new list and updated ${successCount + removedCount - 1} other list${successCount + removedCount - 1 !== 1 ? 's' : ''}`,
                    {
                        position: 'top-center',
                        autoClose: 3000,
                    }
                );
            } else if (createdNewList) {
                toast.success(
                    `Created new list "${newListName}" with ${validApps.length} application(s)!`,
                    {
                        position: 'top-center',
                        autoClose: 3000,
                    }
                );
            } else if (successCount > 0 && removedCount > 0) {
                toast.success(
                    `Added to ${successCount} list${successCount !== 1 ? 's' : ''} and removed from ${removedCount} list${removedCount !== 1 ? 's' : ''}`,
                    {
                        position: 'top-center',
                        autoClose: 3000,
                    }
                );
            } else if (successCount > 0) {
                if (invalidApps.length > 0) {
                    toast.warning(
                        `Added ${validApps.length} valid application(s) to ${successCount} list(s). ${invalidApps.length} item(s) were not valid applications.`,
                        {
                            position: 'top-center',
                            autoClose: 5000,
                        }
                    );
                } else {
                    toast.success(
                        `Successfully added ${validApps.length} application(s) to ${successCount} list(s)`,
                        {
                            position: 'top-center',
                            autoClose: 3000,
                        }
                    );
                }
            } else if (removedCount > 0) {
                toast.success(
                    `Successfully removed from ${removedCount} list${removedCount !== 1 ? 's' : ''}`,
                    {
                        position: 'top-center',
                        autoClose: 3000,
                    }
                );
            } else {
                toast.info('No changes made', {
                    position: 'top-center',
                    autoClose: 2000,
                });
            }

            // Call success callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Close modal
            onClose();
        } catch (error) {
            console.error('Error updating lists:', error);
            toast.error('Failed to update subscription lists', {
                position: 'top-center',
                autoClose: 3000,
            });
        } finally {
            setIsAddingToLists(false);
        }
    };

    // Get main application name
    const getMainApplication = () => {
        if (Array.isArray(applications)) {
            return applications[0] || 'N/A';
        }
        return applications || 'N/A';
    };

    // Get additional items (for ChangeDetailsPage affected CIs)
    const getAdditionalItems = () => {
        if (Array.isArray(applications) && applications.length > 1) {
            return applications.slice(1);
        }
        return [];
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '8px',
                    fontFamily: fontFamily
                }
            }}
        >
            <DialogTitle
                style={{
                    fontFamily: fontFamily,
                    fontSize: '18px',
                    fontWeight: 500,
                    borderBottom: '1px solid #e0e0e0',
                }}
            >
                {modalTitle}
            </DialogTitle>
            <DialogContent style={{ padding: '24px', minHeight: '200px' }}>
                {/* Application Info */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: fontFamily,
                            fontWeight: 500,
                            mb: 1
                        }}
                    >
                        {Array.isArray(applications) ? 'CI: ' : 'Application: '}{getMainApplication()}
                    </Typography>

                    {/* Show additional items with checkboxes if applicable */}
                    {allowMultipleSelection && getAdditionalItems().length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontFamily: fontFamily,
                                    fontWeight: 500,
                                    mb: 1
                                }}
                            >
                                Affected {Array.isArray(applications) ? 'CIs' : 'Applications'}:
                            </Typography>
                            <Box sx={{ maxHeight: '150px', overflowY: 'auto' }}>
                                {getAdditionalItems().map((item) => (
                                    <FormControlLabel
                                        key={item}
                                        control={
                                            <Checkbox
                                                checked={selectedItems.includes(item)}
                                                onChange={() => handleItemToggle(item)}
                                                sx={{
                                                    color: '#141414',
                                                    '&.Mui-checked': { color: '#42b0d5' },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: fontFamily }}
                                            >
                                                {item}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </Box>
                        </>
                    )}
                    <Typography
                        variant="caption"
                        sx={{
                            fontFamily: fontFamily,
                            color: '#666',
                            display: 'block',
                            mt: 1
                        }}
                    >
                        {getApplicationsArray().length} item(s) will be added. Only valid applications will be added.
                    </Typography>
                </Box>

                {loadingLists ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                ) : (
                    <>
                        {/* Create New List Section */}
                        <Box sx={{ mb: 2 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => setShowCreateNew(!showCreateNew)}
                                sx={{
                                    color: '#42b0d5',
                                    borderColor: '#42b0d5',
                                    fontFamily: fontFamily,
                                    '&:hover': {
                                        borderColor: '#42b0d5',
                                        backgroundColor: 'rgba(66, 176, 213, 0.04)'
                                    }
                                }}
                            >
                                {showCreateNew ? 'Cancel New List' : '+ Create New List'}
                            </Button>
                            {showCreateNew && (
                                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                                    <TextField
                                        fullWidth
                                        label="List Name"
                                        value={newListName}
                                        onChange={(e) => setNewListName(e.target.value)}
                                        placeholder="Enter list name"
                                        size="small"
                                        sx={{
                                            mb: 2,
                                            '& .MuiInputBase-root': {
                                                fontFamily: fontFamily
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontFamily: fontFamily
                                            }
                                        }}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        label="Description (Optional)"
                                        value={newListDescription}
                                        onChange={(e) => setNewListDescription(e.target.value)}
                                        placeholder="Enter description"
                                        size="small"
                                        multiline
                                        rows={2}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                fontFamily: fontFamily
                                            },
                                            '& .MuiInputLabel-root': {
                                                fontFamily: fontFamily
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Existing Lists Section */}
                        {subscriptionLists.length === 0 ? (
                            <Alert severity="info" sx={{ fontFamily: fontFamily, mt: 2 }}>
                                You don't have any subscription lists yet. Create one above to get started.
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: '#666', fontFamily: fontFamily }}>
                                    Or select from existing lists:
                                </Typography>
                                <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {subscriptionLists.map((list) => {
                                        const hasAnyApp = list.applications?.some(app =>
                                            getApplicationsArray().includes(app.app_name)
                                        );
                                        return (
                                            <FormControlLabel
                                                key={list.list_id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedLists.includes(list.list_id)}
                                                        onChange={() => handleListToggle(list.list_id)}
                                                        sx={{
                                                            color: '#141414',
                                                            '&.Mui-checked': { color: '#42b0d5' },
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                        <Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontFamily: fontFamily,
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {list.list_name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontFamily: fontFamily,
                                                                    color: '#666'
                                                                }}
                                                            >
                                                                {list.app_count || 0} application(s)
                                                                {list.description && ` • ${list.description}`}
                                                            </Typography>
                                                        </Box>
                                                        {hasAnyApp && (
                                                            <Box
                                                                sx={{
                                                                    fontSize: '11px',
                                                                    color: '#4caf50',
                                                                    backgroundColor: '#e8f5e9',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    ml: 1
                                                                }}
                                                            >
                                                                Already added
                                                            </Box>
                                                        )}
                                                    </Box>
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    mb: 1,
                                                    p: 1,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '4px',
                                                    '&:hover': { backgroundColor: '#f5f5f5' }
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions
                style={{
                    padding: '16px 24px',
                    borderTop: '1px solid #e0e0e0',
                }}
            >
                <button
                    onClick={onClose}
                    className="animated-search-button"
                    disabled={isAddingToLists}
                    style={{
                        backgroundColor: '#f5f5f5',
                        color: '#333',
                        padding: '8px 20px',
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={handleAddToLists}
                    className="animated-search-button"
                    disabled={isAddingToLists || !hasChanges()}
                    style={{
                        backgroundColor: hasChanges() ? '#141414' : '#ccc',
                        color: 'white',
                        padding: '8px 20px',
                    }}
                >
                    {isAddingToLists ? (
                        <>
                            <CircularProgress size={16} style={{ color: 'white', marginRight: '8px' }} />
                            Updating...
                        </>
                    ) : (
                        'Save Changes'
                    )}
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default AddToSubscriptionListModal;
